// Minimal markdown → React renderer. Built for the two docs in
// /docs (ARCHITECTURE.md + SUPER_ADMIN.md) — handles the subset of
// markdown those files actually use, no library dependency.
//
// Supported:
//   - Headings (#, ##, ###, ####). Each gets an id from a slug of
//     the text so the TOC sidebar can scroll to it.
//   - Fenced code blocks (```lang).
//   - Inline code (`code`).
//   - Bold (**text**) and italic (*text*).
//   - Links ([text](url)).
//   - Unordered lists (-, *, +).
//   - Tables (| col | col |\n|---|---|).
//   - Horizontal rules (---).
//   - Paragraphs separated by blank lines.
//
// Intentionally NOT supported (the docs don't use them):
//   - Nested lists, blockquotes, footnotes, images, ordered lists
//     beyond simple "1." (degrades to a bullet), HTML passthrough.

import React from 'react';

export function slugify(text) {
  return String(text).toLowerCase().replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-').slice(0, 80);
}

// Inline span-level rewriter. Order matters: code first (so the rest
// don't touch code contents), then bold (`**`), then italic (`*`),
// then links. Returns an array of React nodes.
function renderInline(text, keyPrefix) {
  // Split into segments around `code` tokens so we can render those
  // verbatim and treat the surrounding pieces as styleable.
  const codeSplit = String(text).split(/(`[^`]+`)/g);
  const out = [];
  codeSplit.forEach((seg, i) => {
    if (/^`[^`]+`$/.test(seg)) {
      out.push(<code key={`${keyPrefix}-c-${i}`} style={{ background: '#f3f4f6', padding: '1px 5px', borderRadius: 3, fontSize: '0.9em' }}>{seg.slice(1, -1)}</code>);
      return;
    }
    let working = seg;
    // Process bold + italic + links in one pass each — apply on the
    // string then render the remaining segments recursively if any
    // tokens remain. For simplicity we just iterate.
    //
    // Bold first because **x** also contains *x* substrings.
    const boldParts = working.split(/(\*\*[^*]+\*\*)/g);
    boldParts.forEach((part, j) => {
      if (/^\*\*[^*]+\*\*$/.test(part)) {
        out.push(<strong key={`${keyPrefix}-b-${i}-${j}`}>{part.slice(2, -2)}</strong>);
        return;
      }
      // Italic: single * around non-* text. Avoid matching ** by
      // requiring no adjacent *.
      const italicParts = part.split(/(\*[^*\s][^*]*\*)/g);
      italicParts.forEach((p2, k) => {
        if (/^\*[^*\s][^*]*\*$/.test(p2)) {
          out.push(<em key={`${keyPrefix}-i-${i}-${j}-${k}`}>{p2.slice(1, -1)}</em>);
          return;
        }
        // Links: [text](url)
        const linkParts = p2.split(/(\[[^\]]+\]\([^)]+\))/g);
        linkParts.forEach((p3, m) => {
          const linkMatch = p3.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
          if (linkMatch) {
            const [, label, href] = linkMatch;
            out.push(<a key={`${keyPrefix}-l-${i}-${j}-${k}-${m}`} href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#0F62FE' }}>{label}</a>);
            return;
          }
          if (p3) out.push(<React.Fragment key={`${keyPrefix}-t-${i}-${j}-${k}-${m}`}>{p3}</React.Fragment>);
        });
      });
    });
  });
  return out;
}

// Block-level parse. Returns { nodes, toc } where toc is a flat list
// of { level, text, slug } for the sidebar.
export function parseMarkdown(md) {
  const lines = String(md || '').split(/\r?\n/);
  const nodes = [];
  const toc = [];

  let i = 0;
  let blockKey = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Code block
    const fence = line.match(/^```(\w*)$/);
    if (fence) {
      const lang = fence[1] || '';
      const body = [];
      i++;
      while (i < lines.length && !/^```\s*$/.test(lines[i])) {
        body.push(lines[i]);
        i++;
      }
      i++; // skip closing fence
      nodes.push(
        <pre key={`code-${blockKey++}`} style={{ background: '#0d1117', color: '#e6edf3', padding: 14, borderRadius: 6, overflowX: 'auto', fontSize: 12, lineHeight: 1.55 }}>
          <code data-lang={lang}>{body.join('\n')}</code>
        </pre>
      );
      continue;
    }

    // Heading
    const heading = line.match(/^(#{1,6})\s+(.+?)\s*$/);
    if (heading) {
      const level = heading[1].length;
      const text = heading[2];
      const slug = slugify(text);
      toc.push({ level, text, slug });
      const sizes = { 1: 28, 2: 22, 3: 18, 4: 15, 5: 14, 6: 13 };
      const weights = { 1: 800, 2: 700, 3: 600, 4: 600, 5: 600, 6: 600 };
      const Tag = `h${level}`;
      nodes.push(
        React.createElement(Tag, {
          key: `h-${blockKey++}`, id: slug,
          style: {
            fontSize: sizes[level], fontWeight: weights[level],
            margin: level <= 2 ? '24px 0 10px' : '18px 0 8px',
            color: '#15172b', scrollMarginTop: 80,
            borderBottom: level <= 2 ? '1px solid #eee' : 'none',
            paddingBottom: level <= 2 ? 6 : 0,
          },
        }, renderInline(text, `h${blockKey}`))
      );
      i++;
      continue;
    }

    // Horizontal rule
    if (/^---+\s*$/.test(line) && i > 0 && !lines[i - 1].includes('|')) {
      nodes.push(<hr key={`hr-${blockKey++}`} style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '20px 0' }} />);
      i++;
      continue;
    }

    // Table — detect "| col |" followed by "|---|"
    if (/^\|.+\|\s*$/.test(line) && i + 1 < lines.length && /^\|[\s:|-]+\|\s*$/.test(lines[i + 1])) {
      const headerCells = line.split('|').slice(1, -1).map((c) => c.trim());
      i += 2; // skip header + delimiter
      const rows = [];
      while (i < lines.length && /^\|.+\|\s*$/.test(lines[i])) {
        rows.push(lines[i].split('|').slice(1, -1).map((c) => c.trim()));
        i++;
      }
      nodes.push(
        <div key={`tbl-${blockKey++}`} style={{ overflowX: 'auto', margin: '12px 0' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 13, width: '100%' }}>
            <thead>
              <tr>
                {headerCells.map((c, ci) => (
                  <th key={ci} style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '2px solid #d1d5db', background: '#fafbfc', fontWeight: 600 }}>
                    {renderInline(c, `th-${ci}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((c, ci) => (
                    <td key={ci} style={{ padding: '6px 10px', borderBottom: '1px solid #f1f1f1', verticalAlign: 'top' }}>
                      {renderInline(c, `td-${ri}-${ci}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Bullet list
    if (/^[-*+]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*+]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*+]\s+/, ''));
        i++;
      }
      nodes.push(
        <ul key={`ul-${blockKey++}`} style={{ margin: '8px 0', paddingLeft: 22 }}>
          {items.map((it, k) => (
            <li key={k} style={{ marginBottom: 4, fontSize: 14, lineHeight: 1.55 }}>
              {renderInline(it, `li-${k}`)}
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // Numbered list (rendered as ordered for accuracy)
    if (/^\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, ''));
        i++;
      }
      nodes.push(
        <ol key={`ol-${blockKey++}`} style={{ margin: '8px 0', paddingLeft: 22 }}>
          {items.map((it, k) => (
            <li key={k} style={{ marginBottom: 4, fontSize: 14, lineHeight: 1.55 }}>
              {renderInline(it, `oli-${k}`)}
            </li>
          ))}
        </ol>
      );
      continue;
    }

    // Empty line → paragraph break (handled by skipping)
    if (line.trim() === '') { i++; continue; }

    // Paragraph: accumulate until blank line / heading / list / fence
    const paraLines = [line];
    i++;
    while (i < lines.length &&
           lines[i].trim() !== '' &&
           !/^#{1,6}\s+/.test(lines[i]) &&
           !/^[-*+]\s+/.test(lines[i]) &&
           !/^\d+\.\s+/.test(lines[i]) &&
           !/^```/.test(lines[i]) &&
           !/^\|.+\|\s*$/.test(lines[i])) {
      paraLines.push(lines[i]);
      i++;
    }
    nodes.push(
      <p key={`p-${blockKey++}`} style={{ margin: '8px 0', fontSize: 14, lineHeight: 1.6, color: '#374151' }}>
        {renderInline(paraLines.join(' '), `p${blockKey}`)}
      </p>
    );
  }

  return { nodes, toc };
}
